
import {
  useState,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import styled from "styled-components";
import MUILink from "@mui/material/Link";
import { useHistory, useLocation } from "react-router";
import { SelectedType } from '@atlaskit/tabs/types';
import { URL } from "../../constants/navigation";
import { ExternalHyperLink } from "../../common/component/NavLink";
import { MainLayout } from "../../layout/MainLayout";
import { Grid } from "../../UI/Grid";
import { Tabs } from "../../UI/Tabs";
import { TabList } from "../../UI/TabList";
import { Tab } from "../../UI/Tab";
import { TabPanel } from "../../UI/TabPanel";
import { Typography } from "../../UI/Typography";
import { TablePaginationProvider } from "../../components/TablePagination/context";
import { handleNavigation } from "../../helpers/handleNavigation";

import { HousingOptions } from "../../components/HousingOptions";
import { AcademicMajors } from "../../components/AcademicMajors";
import { LifestyleQuestion } from '../../components/LifestyleQuestion';
import { Gender } from "../../components/Gender";
import { KnowBeforeRoomsyncRulesTable } from "../../components/KnowBeforeRoomsyncRulesTable";

const tabs = [
  "lifestyle-questions",
  "academic-majors",
  "housing-options",
  "gender",
  "know-before-roomsync"
];

export const Link = styled(MUILink)`
  cursor: pointer;
  display: inline-block;
  color: #007bff !important;
`;

const RESIDENT_EXAMPLE_IMG = "/static/images/resident_example.png";

export const PotentialRoommateFilterPage = () => {
  const [ selectedTab, setSelectedTab ] = useState(0);

  const { push } = useHistory();
  const location = useLocation();

  const navTo = (url:string) => (event: React.MouseEvent) => {
    handleNavigation(event, url, push);
  };

  const handleUpdate = useCallback(
    (index: SelectedType) => {
      setSelectedTab(index);
      push(URL.PROFILE_BASE + tabs[index]);
    },
    [setSelectedTab],
  );

  // Set initial tab based on URL
  useEffect(() => {
    const currentPath = location.pathname;
    const tabSegment = currentPath.replace(URL.PROFILE_BASE, '');
    const tabIndex = tabs.indexOf(tabSegment);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  }, [location.pathname]);

  const HOUSING_DESCRIPTION = useMemo (() => <>
  When this feature is enabled, users have the option to choose one or multiple housing alternatives from the provided list.
  Users are not required to make a selection.
  If you need assistance with updating this list, please email <Link href={`mailto:clients@roomsync.com`}>clients@roomsync.com</Link>
  </>, []);
  
  const MAJORS_DESCRIPTION = useMemo(() => <>
    When this feature is enabled, users have the option to choose one or multiple housing alternatives from the provided list. 
    Users are not required to make a selection. 
    If you need assistance with updating this list, please email <Link href={`mailto:clients@roomsync.com`}>clients@roomsync.com</Link>
  </>, []);

  const LIFESTYLE_QUESTIONS_DESCRIPTION = useMemo(() => <>
    Completion of all lifestyle questions is mandatory for users. These questions come in three formats: slider scale, selection, and open text. 
    To get an idea of how these questions will appear in the app, simply click the 'Preview Questions' button located at the screen's upper section. 
    If you need assistance with updating your questions, please email <Link href={`mailto:clients@roomsync.com`}>clients@roomsync.com</Link>.
  </>, []);

  const KNOWN_BEFORE_ROOMSYNC_RULES_DESCRIPTION = useMemo(() => <>
    When enabled, users will be asked if they knew their roommate beforehand or met through RoomSync. 
    The answers provided by residents will be visible to administrators in each user's profile under the 'Roommates' tab and 
    as part of the exportable <Link onClick={navTo(URL.USER_REPORT)}>User Report</Link>. 
    This can be useful for tracking how many users are meeting on the app and when working to honor roommate requests.&nbsp;
    <ExternalHyperLink to={RESIDENT_EXAMPLE_IMG} name="Here is an example of what this looks like for residents when enabled." />
  </>, []);

const GENDERS_DESCRIPTION = "This tool assists administrators in personalizing the presentation of gender within the application. The app will display to users the provided title, explanatory text, and gender choices. Additionally, it allows the establishment of guidelines for gender-inclusive matching and the presentation of personal pronouns.";

  return (
    <MainLayout title="Profile">
      
      <Tabs
        id="match-rules-tabs"
        shouldUnmountTabPanelOnChange
        onChange={handleUpdate}
        selected={selectedTab}
      >
            <TabList>
              <Tab>Lifestyle Questions</Tab>
              <Tab>Academic Majors</Tab>
              <Tab>Housing Options</Tab>
              <Tab>Gender</Tab>
              <Tab>Knew Before Roomsync</Tab>
            </TabList>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {LIFESTYLE_QUESTIONS_DESCRIPTION}
                </Typography>
                  <LifestyleQuestion />
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {MAJORS_DESCRIPTION}
                </Typography>
                  <AcademicMajors />
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {HOUSING_DESCRIPTION}
                </Typography>
                  <HousingOptions />
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {GENDERS_DESCRIPTION}
                </Typography>
                  <Gender />
              </Grid>
            </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {KNOWN_BEFORE_ROOMSYNC_RULES_DESCRIPTION}
                </Typography>
                <TablePaginationProvider>
                  <KnowBeforeRoomsyncRulesTable />
                </TablePaginationProvider>
              </Grid>
            </TabPanel>
          </Tabs>
    </MainLayout>
  );
};
